import * as React from 'react';
import { AuthContext } from '@/components/layout/AuthContext';
import type { UseAuthReturn } from './types';
import { refreshAuthToken } from '@/api/tokenRefresher';

/**
 * Hook to access the authentication context
 * Provides user state, login/register methods, and loading status
 */
export function useAuth(): UseAuthReturn {
  const ctx = React.useContext(AuthContext);
  if (!ctx) {
    throw new Error('AuthContext not found. Ensure the app is wrapped with AuthProvider.');
  }
  return ctx;
}

/**
 * Hook to automatically refresh auth tokens before they expire.
 * Handles both proactive refresh (before expiry) and recovery (after expiry).
 * Call this in your main authenticated layout component.
 */
export function useTokenRefresh() {
  const { logout } = useAuth();

  const refreshToken = React.useCallback(async () => {
    const token = localStorage.getItem('auth_token');
    if (!token) return;

    try {
      const parts = token.split('.');
      if (parts.length !== 3) return;

      const payloadPart = parts[1];
      if (!payloadPart) return;

      const payload = JSON.parse(atob(payloadPart)) as { exp?: number; iat?: number };
      if (!payload.exp) return;

      const expMs = payload.exp * 1000;
      const timeUntilExpiry = expMs - Date.now();

      // Skip refresh if token is still valid for more than 5 minutes
      if (timeUntilExpiry >= 5 * 60 * 1000) return;

      // Token is either expired or expiring soon — attempt refresh
      // Backend accepts expired tokens up to 7 days old
      const newToken = await refreshAuthToken();

      // eslint-disable-next-line sonarjs/no-collapsible-if -- intentionally split: the else comment explains the no-op branch
      if (!newToken) {
        // Refresh failed and token is already expired — force logout
        if (timeUntilExpiry < 0) {
          logout();
        }
        // If token is still valid (just < 5 min left) but refresh failed
        // (e.g. network error), don't logout — try again next interval
      }
    } catch {
      // Unexpected error — don't logout, retry next interval
    }
  }, [logout]);

  React.useEffect(() => {
    const interval = setInterval(refreshToken, 60 * 1000);
    refreshToken();
    return () => clearInterval(interval);
  }, [refreshToken]);
}
